import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import AppPaths from '../../../../AppPaths';
import { useHistory, useLocation } from 'react-router-dom';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import Button from '../../../../components/Button';
import Chooser from '../../../../components/Chooser';
import { useQuery } from '@apollo/client';
import { getLoadingOrErrorElement } from '../../../../lib/util';
import { petsWithActiveSubscriptionQuery, hasMultidogDiscountQuery } from '../../../../graphql-operations';
import { inAppCancellation as events } from '../../../../lib/analytics/events';
import { gqlTypes } from '../../../../types';
import { SeriesAbbrev } from '../../../../types/Series';
import { useZendeskCancellationTicket } from '../hooks/useZendesk';
import { maybeTruncatePetName } from '../util/helpers';
import { BaseInAppCancellationState } from '../util/types';
import CancelConfirmationModal from '../../components/CancelConfirmationModal';
import ErrorScreen from '../../components/ErrorScreen/ErrorScreen';

export default function CancelSubscription() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<BaseInAppCancellationState>();
  const { subscriptionCategory } = location.state;
  const { device } = usePurchaseSubscriptionContext();
  const [selectedCancelReason, setSelectedCancelReason] = useState<gqlTypes.zendeskCancellationReason | undefined>(
    undefined,
  );

  const showUpgradeForModuleVersions: SeriesAbbrev[] = ['S1', 'S2'];
  const provideReplaceOptionForModuleVersions: SeriesAbbrev[] = ['S3'];
  const collarModel = device.currentCollarModel as SeriesAbbrev;

  const petName = device.pet?.name ? device.pet.name : t('your_dog');
  const shortPetName = maybeTruncatePetName(petName); // 'your dog' will not be truncated as it is < 16 characters

  const query = useQuery<gqlTypes.ECOMMERCE_petsWithActiveSubscription>(petsWithActiveSubscriptionQuery, {});
  const multidogDiscountQuery = useQuery<gqlTypes.ECOMMERCE_hasMultidogDiscount>(hasMultidogDiscountQuery, {});

  const { mutation, loading } = useZendeskCancellationTicket({
    analyticsEvent: events.cancelFromReasonPage,
    selectedCancelReason: selectedCancelReason ?? gqlTypes.zendeskCancellationReason.OTHER,
    device,
    subscriptionCategory,
  });
  if (loading) {
    return loading;
  }

  const loadingOrErrorElement = getLoadingOrErrorElement(
    query.loading || multidogDiscountQuery.loading,
    query.error || multidogDiscountQuery.error,
  );
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }

  if (!device.subscription?.id) {
    return <ErrorScreen />;
  }

  // Cancellation reasons that result in navigation to another page (vs immediate cancellation)
  const navigationReasons = [
    gqlTypes.zendeskCancellationReason.OTHER,
    gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY,
    gqlTypes.zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN,
    gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
    gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY,
    gqlTypes.zendeskCancellationReason.BILLING_ERROR,
    gqlTypes.zendeskCancellationReason.DOG_WILL_NOT_WEAR_COLLAR,
  ];

  const handleNavigate = () => {
    if (!selectedCancelReason || !device.subscription?.id) {
      throw new Error('Something went wrong.');
    }

    events.cancellationReasonSelectedAndClicked({
      petId: device.pet?.id,
      cancellationReason: selectedCancelReason,
      subscriptionCategory,
    });

    if (selectedCancelReason === gqlTypes.zendeskCancellationReason.OTHER) {
      history.push({
        pathname: AppPaths.Subscription.CancelOtherReason(device.moduleId),
        search: window.location.search,
        state: { subscriptionCategory },
      });
    } else if (selectedCancelReason === gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY) {
      history.push({
        pathname: AppPaths.Subscription.TellUsMore(device.moduleId),
        state: {
          selectedCancelReason,
          subscriptionCategory,
        },
        search: window.location.search,
      });
    } else if (selectedCancelReason === gqlTypes.zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN) {
      if (provideReplaceOptionForModuleVersions.includes(collarModel)) {
        history.push({
          pathname: AppPaths.Subscription.Replace(device.moduleId),
          state: {
            selectedCancelReason,
            subscriptionCategory,
          },
          search: window.location.search,
        });
      } else if (showUpgradeForModuleVersions.includes(collarModel)) {
        history.push({
          pathname: AppPaths.Subscription.Upgrade(device.moduleId),
          state: {
            selectedCancelReason,
            subscriptionCategory,
          },
          search: window.location.search,
        });
      }
    } else if (selectedCancelReason === gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE) {
      history.push({
        pathname: AppPaths.Subscription.TooExpensive(device.moduleId),
        search: window.location.search,
        state: { subscriptionCategory },
      });
    } else if (selectedCancelReason === gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY) {
      history.push({
        pathname: AppPaths.Subscription.DogDoesNotRunAway(device.moduleId),
        search: window.location.search,
        state: { subscriptionCategory },
      });
    } else if (selectedCancelReason === gqlTypes.zendeskCancellationReason.BILLING_ERROR) {
      history.push({
        pathname: AppPaths.Subscription.ContactCx(device.moduleId),
        search: window.location.search,
        state: { selectedCancelReason, subscriptionCategory },
      });
    } else if (selectedCancelReason === gqlTypes.zendeskCancellationReason.DOG_WILL_NOT_WEAR_COLLAR) {
      history.push({
        pathname: AppPaths.Subscription.MeetMakers(device.moduleId),
        state: {
          selectedCancelReason,
          subscriptionCategory,
        },
        search: window.location.search,
      });
    } else {
      throw new Error('Something went wrong.');
    }
  };

  const handleCancel = () => {
    if (!selectedCancelReason || !device.subscription?.id) {
      throw new Error('Something went wrong.');
    }

    events.cancellationReasonSelectedAndClicked({
      petId: device.pet?.id,
      cancellationReason: selectedCancelReason,
      subscriptionCategory,
    });

    mutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription.id,
          cancellationReason: selectedCancelReason,
          additionalText: undefined,
        },
      },
    });
  };

  const handleTroubleshoot = () => {
    events.troubleshootButtonClicked({});
    history.push({
      pathname: AppPaths.Troubleshoot.Start(device.moduleId),
      search: window.location.search,
      state: { previousPath: history.location },
    });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.backFromReasonPage({ petId: device.pet?.id, subscriptionCategory })}
          closeButtonAnalyticsEvent={() => events.closeFromReasonPage({ petId: device.pet?.id, subscriptionCategory })}
        />
        <div className={styles.subscriptionContainer}>
          <h1>{t('whats_up_with_pet_name_collar', { petName: shortPetName })}</h1>
          <p>{t('we_dont_want_you_and_pet_name_to_leave_but_your_feedback_can_still_help_us_improve', { petName })}</p>

          <div className={styles.reasonHeader}>{t('reason_for_cancellation')}</div>
          <div className={styles.content}>
            <Chooser
              onSelect={(selectedReason) => {
                setSelectedCancelReason(selectedReason);
              }}
              selectedOption={selectedCancelReason}
              groups={[
                {
                  options: [
                    {
                      content: t('the_collar_doesnt_work_right'),
                      value: gqlTypes.zendeskCancellationReason.COLLAR_DOES_NOT_WORK_PROPERLY,
                    },
                    {
                      content: t('the_collar_is_physically_broken'),
                      value: gqlTypes.zendeskCancellationReason.COLLAR_IS_PHYSICALLY_BROKEN,
                    },
                    {
                      content: t('my_dog_doesnt_like_wearing_the_collar'),
                      value: gqlTypes.zendeskCancellationReason.DOG_WILL_NOT_WEAR_COLLAR,
                    },
                    {
                      content: t('my_dog_doesnt_run_away'),
                      value: gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY,
                    },
                    {
                      content: t('the_membership_is_too_expensive'),
                      value: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
                    },
                    {
                      content: t('there_was_a_billing_error'),
                      value: gqlTypes.zendeskCancellationReason.BILLING_ERROR,
                    },
                    { content: t('other'), value: gqlTypes.zendeskCancellationReason.OTHER },
                  ],
                },
              ]}
              showRadio
              compact
            />
          </div>
        </div>
        <div className={styles.buttonContainer}>
          {selectedCancelReason && navigationReasons.includes(selectedCancelReason) ? (
            <Button className={styles.button} disabled={selectedCancelReason === undefined} onClick={handleNavigate}>
              {t('continue')}
            </Button>
          ) : (
            <CancelConfirmationModal
              onContinue={() => handleCancel()}
              pageName={t('manage_membership')}
              petId={device.pet!.id}
              trigger={
                <Button className={styles.button} disabled={selectedCancelReason === undefined}>
                  {t('continue')}
                </Button>
              }
            />
          )}
          <Button onClick={handleTroubleshoot} className={styles.button} tertiary={true}>
            {t('need_collar_help_troubleshoot')}
          </Button>
        </div>
      </div>
    </>
  );
}
